import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Upload, Link as LinkIcon, X, Loader2, Image as ImageIcon } from "lucide-react";

/**
 * Image input with two modes: upload from device or paste URL.
 * Value is always a string URL.
 *
 * @param {string} [recommended] — short hint shown under the dropzone &
 *   above the URL input, e.g. "1920×640 · WEBP/JPG · ~200KB". Use it to tell
 *   admins which dimensions / format the slot expects.
 */
export default function ImageUploader({ value, onChange, placeholder = "https://...", testId = "image-uploader", recommended = null, allowVideo = false }) {
    const [mode, setMode] = useState(value && value.startsWith("/api/uploads/") ? "upload" : "url");
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [drag, setDrag] = useState(false);
    const fileRef = useRef(null);

    const uploadFile = async (file) => {
        setError("");
        if (!file.type.startsWith("image/")) {
            setError("Please choose an image file");
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/admin/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
        } catch (e) {
            setError(e.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) uploadFile(f);
    };

    const clearImage = () => {
        onChange("");
    };

    return (
        <div className="space-y-2" data-testid={testId}>
            {recommended && (
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 bg-slate-50 border-l-2 border-[hsl(var(--primary))] px-2 py-1" data-testid={`${testId}-recommended`}>
                    Recommended: <span className="text-slate-700 normal-case">{recommended}</span>
                </div>
            )}
            {/* Mode toggle */}
            <div className="flex gap-1 text-[11px]">
                <button
                    type="button"
                    onClick={() => setMode("upload")}
                    className={`px-2 py-1 font-display font-bold uppercase tracking-wider border-b-2 transition-colors ${
                        mode === "upload"
                            ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                            : "border-transparent text-slate-500 hover:text-slate-900"
                    }`}
                    data-testid={`${testId}-mode-upload`}
                >
                    <Upload className="w-3 h-3 inline mr-1" /> Upload
                </button>
                <button
                    type="button"
                    onClick={() => setMode("url")}
                    className={`px-2 py-1 font-display font-bold uppercase tracking-wider border-b-2 transition-colors ${
                        mode === "url"
                            ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                            : "border-transparent text-slate-500 hover:text-slate-900"
                    }`}
                    data-testid={`${testId}-mode-url`}
                >
                    <LinkIcon className="w-3 h-3 inline mr-1" /> URL
                </button>
            </div>

            {mode === "upload" ? (
                <div
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={onDrop}
                    onClick={() => fileRef.current?.click()}
                    className={`border-2 border-dashed p-4 cursor-pointer transition-colors text-center ${
                        drag
                            ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)_/_0.05)]"
                            : "border-slate-300 bg-slate-50 hover:border-slate-400"
                    }`}
                    data-testid={`${testId}-dropzone`}
                >
                    <input
                        ref={fileRef}
                        type="file"
                        accept={allowVideo ? "image/*,video/mp4,video/webm,video/quicktime" : "image/*"}
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                        data-testid={`${testId}-file-input`}
                    />
                    {uploading ? (
                        <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                            <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-1 text-slate-500">
                            <Upload className="w-5 h-5" />
                            <div className="text-xs font-mono">Drop {allowVideo ? "image/video" : "image"} or click to select</div>
                            <div className="text-[10px] font-mono text-slate-400">
                                PNG / JPG / WEBP · Max 8MB
                                {allowVideo && <> · MP4 / WEBM / MOV · Max 25MB</>}
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="input-field"
                    data-testid={`${testId}-url-input`}
                />
            )}

            {error && <div className="text-xs text-red-700">{error}</div>}

            {/* Preview */}
            {value ? (
                <div className="relative inline-block group">
                    {/\.(mp4|webm|mov|m4v)(\?|$)/i.test(value) ? (
                        <video
                            src={value}
                            muted
                            loop
                            autoPlay
                            playsInline
                            className="w-24 h-24 object-cover border border-slate-200 bg-slate-50"
                        />
                    ) : (
                        <img
                            src={value}
                            alt=""
                            className="w-24 h-24 object-contain border border-slate-200 bg-slate-50"
                            onError={(e) => (e.currentTarget.style.opacity = "0.2")}
                        />
                    )}
                    <button
                        type="button"
                        onClick={clearImage}
                        className="absolute -top-2 -right-2 bg-red-600 text-white w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`${testId}-clear`}
                        title="Clear"
                    >
                        ×
                    </button>
                </div>
            ) : (
                <div className="w-24 h-24 border border-dashed border-slate-300 flex items-center justify-center text-slate-300">
                    <ImageIcon className="w-6 h-6" />
                </div>
            )}
        </div>
    );
}
